<script>
    import { createEventDispatcher } from 'svelte';
    import { slide } from 'svelte/transition';

    // Collapsible state
    let isExpanded = false;

    function toggleExpanded(event) {
        // Handle both click and keyboard events
        if (event.type === 'keydown' && event.key !== 'Enter' && event.key !== ' ') {
            return;
        }
        isExpanded = !isExpanded;
    }

    // Input values (raw numbers)
    let purchasePrice = '';
    let sellingPrice = '';
    let newHomePrice = '';

    // Formatted display values for inputs
    let formattedPurchasePrice = '';
    let formattedSellingPrice = '';
    let formattedNewHomePrice = '';

    // Reactive calculations
    $: capitalGain = calculateCapitalGain(purchasePrice, sellingPrice);
    $: taxCalculations = calculateTax(purchasePrice, sellingPrice, newHomePrice);
    $: hasInput = purchasePrice || sellingPrice;

    function calculateCapitalGain(purchase, selling) {
        const purchaseVal = parseFloat(purchase) || 0;
        const sellingVal = parseFloat(selling) || 0;
        return sellingVal - purchaseVal;
    }

    function calculateTax(purchase, selling, newHome) {
        const purchaseVal = parseFloat(purchase) || 0;
        const sellingVal = parseFloat(selling) || 0;
        const newHomeVal = parseFloat(newHome) || 0;
        
        const gain = sellingVal - purchaseVal;
        
        if (gain <= 0) {
            return {
                totalTax: 0,
                taxToPayNow: 0,
                deferredGain: 0,
                taxToDefer: 0,
                netAmount: gain,
            };
        }

        const deferralCap = 3000000;
        let deferralBasis = 0;

        if (newHomeVal > 0 && sellingVal > 0) {
             if (newHomeVal >= sellingVal) {
                deferralBasis = gain;
            } else {
                deferralBasis = (newHomeVal / sellingVal) * gain;
            }
        }

        const deferredGain = Math.min(deferralBasis, deferralCap);
        const gainToTaxNow = gain - deferredGain;
        
        const totalTax = gain * 0.22;
        const taxToPayNow = gainToTaxNow * 0.22;
        const taxToDefer = deferredGain * 0.22;
        
        const netAmount = gain - taxToPayNow;

        return { totalTax, taxToPayNow, deferredGain, taxToDefer, netAmount };
    }

    function handleInput(event, field) {
        let value = event.target.value;
        value = value.replace(/[^\d]/g, '');

        if (field === 'purchase') {
            purchasePrice = value;
            formattedPurchasePrice = value ? formatNumberWithSpaces(value) : '';
        } else if (field === 'selling') {
            sellingPrice = value;
            formattedSellingPrice = value ? formatNumberWithSpaces(value) : '';
        } else {
            newHomePrice = value;
            formattedNewHomePrice = value ? formatNumberWithSpaces(value) : '';
        }
    }
    
    function formatNumberWithSpaces(value) {
        return value.replace(/\B(?=(\d{3})+(?!\d))/g, ' ');
    }

    function resetCalculator() {
        purchasePrice = '';
        sellingPrice = '';
        newHomePrice = '';
        formattedPurchasePrice = '';
        formattedSellingPrice = '';
        formattedNewHomePrice = '';
    }

    // Helper function to format large numbers for display in results
    function formatNumberForDisplay(value) {
        if (value === 0) return '0';
        return value.toLocaleString('sv-SE');
    }
</script>

<div class="key-value-table box wide-calculator">
    <div class="header-card" on:click={toggleExpanded} on:keydown={toggleExpanded} role="button" tabindex="0" aria-expanded={isExpanded}>
        <h5><i class="fa-solid fa-calculator fa-xs icon-prefix"></i> Beräkan Reavinstskatt</h5>
        <i class="fa-solid fa-chevron-{isExpanded ? 'up' : 'down'} chevron-icon"></i>
    </div>

    {#if isExpanded}
    <div class="collapsible-content" transition:slide>
        <div class="info-grid">
            <!-- Input fields -->
            <div class="info-card">
                <div class="info-header">
                    <i class="fa-solid fa-home fa-sm icon-detail"></i>
                    <h6>Inköpspris för bostaden</h6>
                </div>
                <div class="input-container">
                    <input
                        type="text"
                        value={formattedPurchasePrice}
                        on:input={(e) => handleInput(e, 'purchase')}
                        class="price-input"
                        placeholder="0"
                    />
                    <span class="currency-label">kr</span>
                </div>
            </div>

            <div class="info-card">
                <div class="info-header">
                    <i class="fa-solid fa-handshake fa-sm icon-detail"></i>
                    <h6>Försäljningspris för bostaden</h6>
                </div>
                <div class="input-container">
                    <input
                        type="text"
                        value={formattedSellingPrice}
                        on:input={(e) => handleInput(e, 'selling')}
                        class="price-input"
                        placeholder="0"
                    />
                    <span class="currency-label">kr</span>
                </div>
            </div>

            <div class="info-card">
                <div class="info-header">
                    <i class="fa-solid fa-key fa-sm icon-detail"></i>
                    <h6>Pris på ny bostad</h6>
                </div>
                <div class="input-container">
                    <input
                        type="text"
                        value={formattedNewHomePrice}
                        on:input={(e) => handleInput(e, 'newHome')}
                        class="price-input"
                        placeholder="0"
                    />
                    <span class="currency-label">kr</span>
                </div>
            </div>
        </div>

        <!-- Results -->
        {#if hasInput}
            <div class="results-section">
                <div class="info-card result-card">
                    <div class="info-header">
                        <i class="fa-solid fa-chart-line fa-sm icon-detail"></i>
                        <h6>Resultat före skatt</h6>
                    </div>
                    <div class="result-value" class:positive={capitalGain > 0} class:negative={capitalGain < 0} class:neutral={capitalGain === 0}>
                        {formatNumberForDisplay(capitalGain)} kr
                    </div>
                </div>

                <div class="info-card result-card">
                    <div class="info-header">
                        <i class="fa-solid fa-file-invoice-dollar fa-sm icon-detail"></i>
                        <h6>Skatt att betala nu</h6>
                    </div>
                    <div class="result-value tax" class:no-tax={taxCalculations.taxToPayNow === 0}>
                        {#if taxCalculations.taxToPayNow > 0}
                            {formatNumberForDisplay(taxCalculations.taxToPayNow)} kr
                        {:else}
                            Ingen skatt nu
                        {/if}
                    </div>
                </div>

                <div class="info-card result-card featured">
                    <div class="info-header">
                        <i class="fa-solid fa-piggy-bank fa-sm icon-detail"></i>
                        <h6>Netto efter skatt</h6>
                    </div>
                    <div class="result-value final" class:positive={taxCalculations.netAmount > 0} class:negative={taxCalculations.netAmount < 0} class:neutral={taxCalculations.netAmount === 0}>
                        {formatNumberForDisplay(taxCalculations.netAmount)} kr
                    </div>
                </div>
            </div>

            {#if taxCalculations.deferredGain > 0}
            <div class="results-section deferral-section">
                <div class="info-card result-card deferral-card">
                    <div class="info-header">
                        <i class="fa-solid fa-calendar-check fa-sm icon-detail"></i>
                        <h6>Uppskjuten vinst (Uppskov)</h6>
                    </div>
                    <div class="result-value neutral">
                        {formatNumberForDisplay(taxCalculations.deferredGain)} kr
                    </div>
                </div>
                <div class="info-card result-card deferral-card">
                    <div class="info-header">
                        <i class="fa-solid fa-pause-circle fa-sm icon-detail"></i>
                        <h6>Uppskjuten skatt</h6>
                    </div>
                    <div class="result-value neutral">
                        {formatNumberForDisplay(taxCalculations.taxToDefer)} kr
                    </div>
                </div>
            </div>
            {/if}

            <!-- Disclaimer -->
            <div class="info-card full-width disclaimer-card">
                <div class="info-header">
                    <i class="fa-solid fa-info-circle fa-sm icon-detail"></i>
                    <h6>Observera</h6>
                </div>
                <div class="info-content">
                    <p>Denna kalkyl är förenklad och inkluderar inte avdrag för förbättringar, försäljningskostnader eller andra faktorer. Uppskovsbeloppet är preliminärt och baseras på ett tak på 3 miljoner kr. Läs mer på <a href="https://www.skatteverket.se/privat/fastigheterochbostad/forsaljningavbostad/raknautvinstforlustochskatt/berakningshjalpfordinbostadsforsaljning.4.4a4d586616058d860bc5131.html" target="_blank" style="color: #6c757d;">Skatteverkets hemsida</a>.</p>
                    <button on:click={resetCalculator} class="reset-button">
                        <i class="fa-solid fa-refresh"></i> Återställ
                    </button>
                </div>
            </div>
        {/if}
    </div>
    {/if}
</div>

<style>
    .header-card {
        background-color: #fff3cd;
        border-radius: 10px 10px 10px 10px;
        padding: 15px 20px;
        border-bottom: 1px solid #e6c200;
        cursor: pointer;
        transition: background-color 0.2s;
        display: flex;
        align-items: center;
        justify-content: space-between;
        user-select: none;
    }

    .header-card:hover {
        background-color: #e6c200;
    }

    .chevron-icon {
        color: #b8860b;
        font-size: 0.9rem;
        transition: transform 0.2s;
    }

    .collapsible-content {
        border-radius: 0 0 10px 10px;
        overflow: hidden;
    }

    .info-grid {
        display: grid;
        grid-template-columns: repeat(auto-fill, minmax(220px, 1fr));
        gap: 20px;
        margin-bottom: 25px;
    }

    .info-card {
        background-color: #f8f9fa;
        border-radius: 10px;
        padding: 20px;
        box-shadow: 0 2px 4px rgba(0, 0, 0, 0.05);
        transition: transform 0.2s, box-shadow 0.2s;
        border: 1px solid #e9ecef;
    }

    .info-card:hover {
        transform: translateY(-3px);
        box-shadow: 0 4px 8px rgba(0, 0, 0, 0.08);
    }

    .info-header {
        display: flex;
        align-items: center;
        margin-bottom: 15px;
    }

    .info-header h6 {
        font-size: 0.9rem;
        color: #495057;
        font-weight: 500;
        margin: 0;
    }

    .input-container {
        position: relative;
        display: flex;
        align-items: center;
    }

    .price-input {
        width: 100%;
        padding: 10px 50px 10px 10px;
        border: 2px solid #dee2e6;
        border-radius: 6px;
        font-size: 1rem;
        font-weight: 500;
        color: var(--box-title-color);
        background-color: white;
        transition: border-color 0.2s;
        text-align: right;
    }

    .price-input:focus {
        outline: none;
        border-color: #007bff;
        box-shadow: 0 0 0 3px rgba(0, 123, 255, 0.1);
    }

    .currency-label {
        position: absolute;
        right: 15px;
        color: #6c757d;
        font-size: 0.95rem;
        font-weight: 500;
        pointer-events: none;
    }

    .results-section {
        display: grid;
        grid-template-columns: repeat(auto-fill, minmax(220px, 1fr));
        gap: 20px;
        margin-bottom: 25px;
    }

    .result-card {
        background-color: #fff;
        border: 2px solid #e9ecef;
    }

    .result-card.featured {
        background-color: #f8f9ff;
        border: 2px solid #4c6ef5;
        box-shadow: 0 4px 12px rgba(76, 110, 245, 0.15);
    }

    .deferral-section {
        margin-top: -5px;
    }

    .deferral-card {
        background-color: #f1f3f5;
        border-color: #dee2e6;
    }

    .result-value {
        font-size: 1.4rem;
        font-weight: 700;
        text-align: center;
        padding: 10px 0;
    }

    .result-value.positive {
        color: #28a745;
    }

    .result-value.negative {
        color: #dc3545;
    }

    .result-value.neutral {
        color: #6c757d;
    }

    .result-value.tax {
        color: #dc3545;
    }

    .result-value.no-tax {
        color: #6c757d;
        font-size: 1rem;
        font-weight: 400;
    }

    .result-value.final {
        font-size: 1.6rem;
        font-weight: 800;
    }

    .info-card.full-width {
        grid-column: 1 / -1;
        margin-top: 0;
    }

    .disclaimer-card {
        background-color: #fff3cd;
        border: 1px solid #ffeaa7;
    }

    .disclaimer-card .info-content p {
        font-size: 0.9rem;
        color: #856404;
        line-height: 1.6;
        margin-bottom: 15px;
    }

    .reset-button {
        background-color: #6c757d;
        color: white;
        border: none;
        padding: 8px 16px;
        border-radius: 6px;
        font-size: 0.9rem;
        cursor: pointer;
        transition: background-color 0.2s;
        display: inline-flex;
        align-items: center;
        gap: 8px;
    }

    .reset-button:hover {
        background-color: #5a6268;
    }

    .reset-button:active {
        transform: translateY(1px);
    }

    /* Make the calculator span 2 columns in the parent grid */
    .wide-calculator {
        grid-column: span 1;
    }

    /* On smaller screens where there's only 1 column, span the full width */
    @media (max-width: 991px) {
        .wide-calculator {
            grid-column: span 1;
        }
    }
</style>
